<template>
  <ion-page v-theme-scope="themeScope">
    <ion-content :fullscreen="true">
      <!--
        Spec 6.7, screen 7: "Two faces of one state."

        ONE component, not two, because it is one state — a hold running on the
        member's bundle — and the spec's two sentences are two views of it. The
        alternative was a screen per face, and the thing that must not happen is
        the two drifting: a promise about deleting a member's notes that reads
        differently depending on which phone they picked up. The sentences
        themselves live in `reset-copy.js`, verbatim from the spec.

        WHICH FACE. `asked` is `pendingReset.requestedBy` matching this
        install's device id — the API records the `X-Device-Id` of the unproven
        POST for exactly this. Not "this device has no key": a device that asked
        while keyless and was approved afterwards still asked, and it is now the
        one that can cancel.

        The screen performs nothing. Cancel emits, and the caller signs `cancel`
        with the bundle key (utils/courses-encryption/reset.js); "This was me"
        emits, and the caller records the dismissal for THIS device only.

        Rendered by the Start over route on the device that asked, and inside a
        modal at app launch on every approved device — the same component in
        both places, which is what makes "two faces of one state" true rather
        than aspirational.
      -->
      <div
        class="flex min-h-full flex-col px-6 pb-10 pt-24 transition-colors duration-300 bg-[var(--courses-surface)]"
      >
        <div class="flex flex-1 flex-col items-start">
          <CoursesBackButton data-testid="reset-pending-back" />
          <h1
            class="text-[2.25rem] font-black leading-[2.5rem] tracking-[-0.03em] transition-colors duration-300 text-[var(--courses-ink)]"
          >
            {{ COPY.heading }}
          </h1>

          <!--
            One element for both faces, and a live region: which face is shown
            can change under a member who is looking at it (a status read
            landing, a cancel from another device), and an announcement fires
            when a region's CONTENT changes — a region only just inserted may
            be announced as nothing at all.
          -->
          <p
            data-testid="reset-pending-sentence"
            role="status"
            aria-live="polite"
            class="mt-4 text-[1.0625rem] leading-[1.5rem] text-[var(--courses-ink)]"
          >
            {{ sentence }}
          </p>

          <!--
            Only on the face of the device that asked. It is a statement of what
            that device can do until the hold ends, and on this device the
            answer is already nothing: it asked unsigned, which is what a device
            with no key does, and the sealing hook refuses every annotation
            write without one. Saying so beats letting the member discover it
            one refused note at a time.
          -->
          <p
            v-if="asked"
            data-testid="note-taking-held"
            class="mt-4 text-[0.9375rem] leading-[1.375rem] text-[var(--courses-ink-muted)]"
          >
            {{ COPY.noteTakingHeld }}
          </p>

          <p
            v-if="message"
            data-testid="reset-pending-error"
            role="alert"
            class="mt-6 text-[0.9375rem] leading-[1.375rem] text-[var(--courses-ink)]"
          >
            {{ message }}
          </p>
        </div>

        <div class="flex w-full max-w-[600px] flex-col items-center gap-4 self-center pt-10">
          <!--
            Shown whenever this device can actually sign a cancel, on EITHER
            face. A device that asked while it held no key and has since been
            approved is both the asker and the only thing that can call the
            reset off; hiding the button from it because of which face it is on
            would leave the member with the one screen that could help them and
            no way to use it.

            `DELETE /reset` answers 403 without a valid signature (decision 16:
            a session thief must not be able to cancel a genuine reset), so a
            device with no key is offered nothing rather than a button whose
            only possible outcome is a refusal.
          -->
          <button
            v-if="canCancel"
            type="button"
            data-testid="reset-pending-cancel"
            class="w-full rounded-full bg-[var(--courses-ink)] py-4 text-[1.0625rem] font-extrabold text-[var(--courses-surface)] disabled:opacity-50"
            :disabled="busy"
            @click="$emit('cancel')"
          >
            {{ busy ? COPY.cancelling : COPY.cancel }}
          </button>

          <!--
            THIS DEVICE ONLY (spec 6.7, screen 7). The member has seen the
            notice and it was them; the reset goes on, and the caller remembers
            the dismissal against this member on this device so a relaunch does
            not ask again. Never offered on the face of the device that asked:
            it already knows it was them.
          -->
          <button
            v-if="!asked"
            type="button"
            data-testid="reset-pending-dismiss"
            class="text-[0.9375rem] font-bold underline text-[var(--courses-ink-muted)]"
            :disabled="busy"
            @click="$emit('dismiss')"
          >
            {{ COPY.wasMe }}
          </button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonContent, IonPage } from '@ionic/vue';
import coursesTheme from '@/mixins/CoursesTheme';
import CoursesBackButton from '@/pages/courses/encryption/CoursesBackButton.vue';
import privateRegion from '@/mixins/PrivateRegion';
import { errorCopy } from '@/pages/courses/encryption/error-copy';
import {
  RESET_PENDING_COPY,
  askedSentence,
  noticeSentence,
} from '@/pages/courses/encryption/reset-copy';

/**
 * A cancel that comes back 404 has already happened, or the hold has run out
 * and the API completed it on this very request (decision 16, lazy completion).
 * Either way the generic "it may already have been done" is exactly right, so
 * only the conflict case needs its own sentence here.
 */
const ERROR_OVERRIDES = Object.freeze({
  conflict: 'The reset has already moved on. Open Next Steps again to see where things stand.',
});

export default {
  name: 'CoursesResetPending',
  components: { IonPage, IonContent, CoursesBackButton },
  mixins: [privateRegion, coursesTheme],
  props: {
    /** `pendingReset` from `GET /api/me/encryption`, verbatim, or null. */
    pendingReset: { type: Object, default: null },
    /**
     * The name of the device that asked, resolved from the status' own device
     * list by the caller (`requestingDeviceName`). Null when there is no row to
     * name, and the spec's own fallback phrase stands in its place.
     */
    deviceName: { type: String, default: null },
    /** True on the device whose id the API recorded as `requestedBy`. */
    asked: { type: Boolean, default: false },
    /**
     * Whether this device holds the key a cancel has to be signed with. The
     * caller reads `courses-keys/hasMasterKey`; the screen never asks, because
     * a screen that guessed would offer a button the API answers 403 to.
     */
    canCancel: { type: Boolean, default: false },
    /** True while the caller signs and sends the cancel. */
    busy: { type: Boolean, default: false },
    /** `courses-devices` state.error, or null. Rendered through `errorCopy`. */
    error: { type: Object, default: null },
  },
  emits: ['cancel', 'dismiss'],
  computed: {
    COPY: () => RESET_PENDING_COPY,
    sentence() {
      return this.asked
        ? askedSentence(this.pendingReset)
        : noticeSentence(this.pendingReset, this.deviceName);
    },
    message() {
      return this.error ? errorCopy(this.error, ERROR_OVERRIDES) : null;
    },
  },
};
</script>
